import { useEffect, useState, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';

const delay = (ms) => new Promise((r) => setTimeout(r, ms));

let globalAudioCtx = null;

const getAudioContext = () => {
  if (!globalAudioCtx) {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (AudioCtx) {
      globalAudioCtx = new AudioCtx();
    }
  }
  if (globalAudioCtx && globalAudioCtx.state === 'suspended') {
    globalAudioCtx.resume();
  }
  return globalAudioCtx;
};

const playRetroSound = (type) => {
  try {
    const ctx = getAudioContext();
    if (!ctx || ctx.state !== 'running') return;

    if (type === 'launch') {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(150, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(850, ctx.currentTime + 0.45);
      gain.gain.setValueAtTime(0.12, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.01, ctx.currentTime + 0.45);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.45);
    } else if (type === 'explode') {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'square';
      osc.frequency.setValueAtTime(220, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(25, ctx.currentTime + 0.4);
      gain.gain.setValueAtTime(0.25, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.01, ctx.currentTime + 0.4);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.4);
    } else if (type === 'pop') {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(300, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(600, ctx.currentTime + 0.08);
      gain.gain.setValueAtTime(0.1, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.01, ctx.currentTime + 0.08);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.08);
    }
  } catch (e) {

  }
};

export default function LoadingScreen({ onDone }) {
  const { isDark } = useTheme();
  const [hasStarted, setHasStarted] = useState(false);
  const [phase, setPhase] = useState('idle');
  const [divOpen, setDivOpen] = useState(false);
  const [showSub, setShowSub] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const [showMiniWelcome, setShowMiniWelcome] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  const canvasRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const launchDualPixelFireworks = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    
    const width = window.innerWidth;
    const height = window.innerHeight;
    canvas.width = width;
    canvas.height = height;

    const targetY = height * 0.45;
    const leftTargetX = width * 0.35;
    const rightTargetX = width * 0.65;

    const leftStartX = width * 0.1;
    const rightStartX = width * 0.9;
    const startY = height;

    const speed = Math.max(12, Math.min(width, height) * 0.02);

    const leftAngle = Math.atan2(targetY - startY, leftTargetX - leftStartX);
    const rightAngle = Math.atan2(targetY - startY, rightTargetX - rightStartX);

    const rockets = [
      {
        x: leftStartX,
        y: startY,
        vx: Math.cos(leftAngle) * speed,
        vy: Math.sin(leftAngle) * speed,
        targetX: leftTargetX,
        targetY: targetY,
        color: '#2563EB',
        trailColor: '#F59E0B',
        exploded: false,
      },
      {
        x: rightStartX,
        y: startY,
        vx: Math.cos(rightAngle) * speed,
        vy: Math.sin(rightAngle) * speed,
        targetX: rightTargetX,
        targetY: targetY,
        color: '#EC4899',
        trailColor: '#10B981',
        exploded: false,
      }
    ];

    playRetroSound('launch');

    const colors = ['#2563EB', '#3B82F6', '#60A5FA', '#1E293B', '#F59E0B', '#EF4444', '#10B981', '#EC4899', '#8B5CF6'];
    const particles = [];

    function animate() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      let allRocketsExploded = true;

      rockets.forEach((r) => {
        if (!r.exploded) {
          allRocketsExploded = false;
          r.x += r.vx;
          r.y += r.vy;

          ctx.fillStyle = r.color;
          ctx.fillRect(Math.floor(r.x) - 3, Math.floor(r.y) - 3, 6, 6);

          ctx.fillStyle = r.trailColor;
          ctx.fillRect(Math.floor(r.x - r.vx * 0.5) - 2, Math.floor(r.y - r.vy * 0.5) - 2, 4, 4);

          if (r.y <= r.targetY) {
            r.exploded = true;

            for (let i = 0; i < 55; i++) {
              const pAngle = Math.random() * Math.PI * 2;
              const pSpeed = 2 + Math.random() * 7;
              particles.push({
                x: r.x,
                y: r.y,
                vx: Math.cos(pAngle) * pSpeed,
                vy: Math.sin(pAngle) * pSpeed,
                size: 4 + Math.floor(Math.random() * 4),
                color: colors[Math.floor(Math.random() * colors.length)],
                alpha: 1,
                decay: 0.015 + Math.random() * 0.015
              });
            }
          }
        }
      });

      if (allRocketsExploded && particles.length > 0 && !canvas.dataset.exploded) {
        canvas.dataset.exploded = 'true';
        playRetroSound('explode');
        setIsShaking(true);
        setTimeout(() => setIsShaking(false), 300);
      }

      let activeParticles = false;
      particles.forEach((p) => {
        if (p.alpha > 0) {
          activeParticles = true;
          p.x += p.vx;
          p.y += p.vy;
          p.vy += 0.12;
          p.alpha -= p.decay;

          ctx.save();
          ctx.globalAlpha = Math.max(p.alpha, 0);
          ctx.fillStyle = p.color;
          ctx.fillRect(Math.floor(p.x), Math.floor(p.y), p.size, p.size);
          ctx.restore();
        }
      });

      if (!allRocketsExploded || activeParticles) {
        requestAnimationFrame(animate);
      }
    }

    animate();
  };

  const handleStartSequence = async () => {
    // Buka izin Web Audio API
    const ctx = getAudioContext();
    if (ctx && ctx.state === 'suspended') {
      await ctx.resume();
    }

    playRetroSound('pop');
    setHasStarted(true);

    await delay(300);

    setPhase('hi');
    playRetroSound('pop');
    await delay(1400);
    setPhase('hi-out');
    await delay(700);

    setPhase('welcome');
    playRetroSound('pop');
    await delay(1400);
    setPhase('welcome-out');
    await delay(700);

    setPhase('final');
    
    launchDualPixelFireworks();
    await delay(800);

    setShowMiniWelcome(true);
    await delay(300);

    setDivOpen(true);
    await delay(500);

    setShowSub(true);

    // Diam sejenak di akhir untuk memperlihatkan animasi secara utuh
    await delay(1800);

    // Otomatis berpindah ke halaman utama
    sessionStorage.setItem('visited', '1');
    setIsExiting(true);
    await delay(800);
    onDone();
  };

  function wordStyle(active, exiting) {
    return {
      position: 'absolute',
      opacity: active && !exiting ? 1 : 0,
      transform: exiting
        ? 'translateY(-12px)'
        : active
        ? 'translateY(0)'
        : 'translateY(10px)',
      transition: 'opacity 0.7s ease, transform 0.7s ease',
      whiteSpace: 'nowrap',
    };
  }

  const isHi = phase === 'hi' || phase === 'hi-out';
  const hiExit = phase === 'hi-out';
  const isWelcome = phase === 'welcome' || phase === 'welcome-out';
  const welExit = phase === 'welcome-out';
  const isFinal = phase === 'final';

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: isDark ? '#060B14' : '#F8FAFC',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Press Start 2P', monospace",
        overflow: 'hidden',
        padding: '16px',
        opacity: isExiting ? 0 : 1,
        pointerEvents: isExiting ? 'none' : 'auto',
        transition: 'opacity 0.8s ease-in-out',
      }}
    >
      <style>{`
        @keyframes shake {
          0% { transform: translate(1px, 1px) rotate(0deg); }
          10% { transform: translate(-1px, -2px) rotate(-1deg); }
          20% { transform: translate(-3px, 0px) rotate(1deg); }
          30% { transform: translate(3px, 2px) rotate(0deg); }
          40% { transform: translate(1px, -1px) rotate(1deg); }
          50% { transform: translate(-1px, 2px) rotate(-1deg); }
          60% { transform: translate(-3px, 1px) rotate(0deg); }
          75% { transform: translate(3px, 1px) rotate(-1deg); }
          90% { transform: translate(1px, 2px) rotate(0deg); }
          100% { transform: translate(1px, -2px) rotate(1deg); }
        }
      `}</style>

      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 2,
          pointerEvents: 'none',
        }}
      />

      <div style={{
        position: 'absolute',
        inset: 0,
        zIndex: 0,
        backgroundSize: '24px 24px',
        backgroundImage: isDark
          ? 'linear-gradient(to right, #1E3A5F 1px, transparent 1px),linear-gradient(to bottom, #1E3A5F 1px, transparent 1px)'
          : 'linear-gradient(to right, #E2E8F0 1px, transparent 1px),linear-gradient(to bottom, #E2E8F0 1px, transparent 1px)',
        opacity: isDark ? 0.3 : 0.5,
      }} />

      {!hasStarted ? (
        <button
          onClick={handleStartSequence}
          style={{
            position: 'relative',
            zIndex: 10,
            fontFamily: "'Press Start 2P', monospace",
            fontSize: 'clamp(10px, 2.5vw, 13px)',
            background: '#1E293B',
            color: '#F8FAFC',
            border: '2px solid #1E293B',
            boxShadow: '6px 6px 0 #2563EB',
            padding: 'clamp(12px, 3vw, 18px) clamp(24px, 6vw, 36px)',
            cursor: 'pointer',
            letterSpacing: '2px',
            transition: 'all 0.1s ease',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.transform = 'translate(-2px,-2px)';
            e.currentTarget.style.boxShadow = '8px 8px 0 #2563EB';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.transform = '';
            e.currentTarget.style.boxShadow = '6px 6px 0 #2563EB';
          }}
          onMouseDown={e => {
            e.currentTarget.style.transform = 'translate(2px,2px)';
            e.currentTarget.style.boxShadow = '3px 3px 0 #2563EB';
          }}
          onMouseUp={e => {
            e.currentTarget.style.transform = '';
            e.currentTarget.style.boxShadow = '6px 6px 0 #2563EB';
          }}
        >
          ▶ START
        </button>
      ) : (
        <div style={{
          position: 'relative',
          zIndex: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '240px',
          width: '100%',
          maxWidth: '600px',
          textAlign: 'center',
        }}>
          <div style={{
            position: 'relative',
            height: 'clamp(60px, 15vw, 80px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
          }}>
            <span style={{
              ...wordStyle(isHi, hiExit),
              fontSize: 'clamp(28px, 7vw, 42px)',
              color: isDark ? '#E2E8F0' : '#1E293B',
              letterSpacing: 'clamp(2px, 1vw, 4px)',
            }}>
              HI!
            </span>

            <span style={{
              ...wordStyle(isWelcome, welExit),
              fontSize: 'clamp(12px, 3.5vw, 18px)',
              color: isDark ? '#3B82F6' : '#2563EB',
              letterSpacing: 'clamp(3px, 1.5vw, 6px)',
            }}>
              WELCOME TO
            </span>

            <div style={{
              ...wordStyle(isFinal, false),
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              animation: isShaking ? 'shake 0.15s infinite' : 'none',
            }}>
              <span style={{
                fontSize: 'clamp(8px, 2vw, 10px)',
                color: isDark ? '#3B82F6' : '#2563EB',
                letterSpacing: 'clamp(2px, 1vw, 4px)',
                marginBottom: '8px',
                opacity: showMiniWelcome ? 1 : 0,
                transform: showMiniWelcome ? 'translateY(0)' : 'translateY(5px)',
                transition: 'opacity 0.5s ease, transform 0.5s ease',
              }}>
                WELCOME TO
              </span>

              <span style={{
                fontSize: 'clamp(18px, 5vw, 36px)',
                color: isDark ? '#E2E8F0' : '#1E293B',
                letterSpacing: 'clamp(1px, 0.8vw, 3px)',
              }}>
                MY WEBSITE
              </span>
            </div>
          </div>

          <div style={{
            height: '3px',
            background: isDark ? '#3B82F6' : '#1E293B',
            width: divOpen ? 'clamp(100px, 40vw, 160px)' : '0px',
            transition: 'width 0.8s ease',
            margin: '20px auto 0',
          }} />

          <div style={{
            fontFamily: "'Roboto Mono', monospace",
            fontSize: 'clamp(9px, 2vw, 10px)',
            color: isDark ? '#64748B' : '#94A3B8',
            letterSpacing: 'clamp(1.5px, 0.5vw, 3px)',
            marginTop: '20px',
            opacity: showSub ? 1 : 0,
            transition: 'opacity 0.7s ease',
          }}>
            NAKHALAN ATQIYA
          </div>
        </div>
      )}
    </div>
  );
}